import { useAppSelector } from "../../store/hooks";

import { useLocation, useNavigate } from "react-router-dom";
import { logErrorToBackend } from "./utilities/helpers";
import ContentRenderWebpageComponent from "./ContentRenderWebpageComponent";
import "./Default.css";
import "./ShopTheme.css";
import { useEffect } from "react";
import CookiePolicyComponent from "./CookiePolicyComponent";
import ShoppingCartExpired from "./ShoppingCart/ShoppingCartExpired";
import React from "react";
import { getPageLocaleUrl } from "./utilities/helpers";
import {
  languageCodeFromActiveLanguage,
  languageCodesEqual,
} from "./utilities/languageCode";
import { shouldRedirectPathname } from "./utilities/redirectLogic";

declare var defaultComponents: any;

export default function ContentRenderer() {
  const fontUrl = useAppSelector((state) => state.builder.font_url.url);
  const webpageComponents = useAppSelector(
    (state) => state.builder.structure.webpageComponents
  );

  const pages = useAppSelector((state) => state.builder.pages.list);
  const website = useAppSelector((state) => state.builder.website);
  const colors = useAppSelector((state) => state.builder.colors);
  const navigate = useNavigate();
  const pageLanguages = useAppSelector(
    (state) => state.builder.languageSettings.activeLanguages
  );
  const isBuilder = document.getElementById("BuilderContent");

  useEffect(() => {
    if (colors) {
      const shopThemeColor = colors.find((x) => x.name === "SHOPTHEMECOLOR");

      if (shopThemeColor?.color) {
        document.documentElement.style.setProperty(
          "--shop-theme-color",
          shopThemeColor?.color
        );
      }
    }
  }, [colors]);

  useEffect(() => {
    if (colors) {
      const shopThemeColor = colors.find(
        (x) => x.name === "SHOPTHEMECOLORHOVER"
      );

      if (shopThemeColor?.color) {
        document.documentElement.style.setProperty(
          "--shop-theme-color-hover",
          shopThemeColor?.color
        );
      }
    }
  }, [colors]);

  useEffect(() => {
    if (website) {
      document.documentElement.style.setProperty(
        "--shop-border-radius",
        website.shopBRadius ? website.shopBRadius : "0px"
      );
    }
  }, [website]);

  const location = useLocation();

  if (
    !window.location.href.includes("/builder/") &&
    location.key === "default" &&
    location.pathname !== "/"
  ) {
    const pageNotFoundData = {
      name: "PageNotFound",
      message: location.pathname,
      stack:
        "Page with pathname " +
        location.pathname +
        " was not found. In ContentRenderer.tsx",
      componentStack: location.pathname,
      href: window.location.href,
    };
    logErrorToBackend(pageNotFoundData);
  }

  useEffect(() => {
    if (!isBuilder && pages) {
      const currentPathname = location.pathname;
      const languages = navigator.languages;
      let firstLanguage = languages[0];

      if (firstLanguage.includes("-")) {
        firstLanguage = firstLanguage.split("-")[0];
      }

      if (
        (currentPathname === "/" || currentPathname === "") &&
        pageLanguages &&
        pageLanguages.length > 0
      ) {
        const isVisitorEnabled = (language: {
          visitor_state?: number;
          visitorState?: number;
        }) =>
          Number(language?.visitor_state ?? language?.visitorState ?? 0) === 1;

        const activeLanguages = pageLanguages.filter(
          (x) => isVisitorEnabled(x)
        );

        const homepages = pages
          .filter((page) => page.pageType === "frontPage")
          .filter((page) => {
            const pagesupDate = activeLanguages.filter(
              (activeLanguage) =>
                languageCodesEqual(
                  page.languageCode,
                  languageCodeFromActiveLanguage(activeLanguage)
                )
            );

            return pagesupDate.length > 0;
          });

        if (!homepages.length) {
          return;
        }

        let selectedHomePage = homepages[0];

        if (website.useBrowserLanguage === 1) {
          const homepageFirstLanguage = homepages.find((page) =>
            languageCodesEqual(page.languageCode, firstLanguage)
          );

          if (!homepageFirstLanguage && languages.length >= 2) {
            let secondLanguage = languages[1] ?? "";

            if (secondLanguage && secondLanguage.includes("-")) {
              secondLanguage = secondLanguage.split("-")[0];
            }

            selectedHomePage = homepages.find(
              (page) => languageCodesEqual(page.languageCode, secondLanguage)
            ) ?? selectedHomePage;
          } else if (homepageFirstLanguage) {
            selectedHomePage = homepageFirstLanguage;
          }
        } else if (website.useBrowserLanguage === 0 && pageLanguages) {
          const firstVisitorLanguage = activeLanguages[0];
          const targetCode = languageCodeFromActiveLanguage(firstVisitorLanguage);
          if (targetCode) {
            selectedHomePage =
              homepages.find((page) =>
                languageCodesEqual(page.languageCode, targetCode)
              ) ?? selectedHomePage;
          }
        }

        const nextUrl = getPageLocaleUrl(selectedHomePage, pageLanguages);
        if (shouldRedirectPathname(currentPathname, nextUrl)) {
          navigate(nextUrl, { replace: true });
        }
      }
    }
  }, [pageLanguages, isBuilder, pages, location.pathname, navigate, website]);

  // Read cookie

  const renderComponents = () => {
    return webpageComponents?.map((rootKey: string | number) => {
      const keyStr = String(rootKey);
      return (
        <ContentRenderWebpageComponent
          key={keyStr}
          componentKey={rootKey}
        />
      );
    });
  };
  // Empty dependency array to run this effect only once on mount

  return (
    <>
      {fontUrl && (
        <>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link
            rel="preconnect"
            href="https://fonts.gstatic.com"
            crossOrigin="anonymous"
          />
          <link href={fontUrl} rel="stylesheet" />
        </>
      )}
      <div
        className="ContentRenderer"
        style={{ overflow: isBuilder ? "visible" : "clip" }}
      >
        {renderComponents()}
      </div>
      <CookiePolicyComponent />
      <ShoppingCartExpired />
    </>
  );
}
